import type { FloatResolver } from '@pct-ts/typing';
import { isFloat } from '@pct-ts/typeguard';

/**
 * Resolves a float value based on a resolver function or a static float.
 *
 * This function accepts either a float directly or a function that returns a float.
 * If a float is provided, it's returned as-is. If a function is provided, it's called
 * with the given parameter to generate the float value.
 *
 * @param resolver - A float value or a function that resolves to a float
 * @param param - The parameter to pass to the resolver function
 * @returns The resolved float value
 *
 * @example
 * // Using a static float
 * const static = resolveFloat(3.14, {});
 * console.log(static); // Output: 3.14
 *
 * @example
 * // Using a resolver function
 * const dynamic = resolveFloat(
 *   (item: { value: number }) => item.value * 1.5,
 *   { value: 10.0 }
 * );
 * console.log(dynamic); // Output: 15.0
 */
export function resolveFloat<T>(resolver: FloatResolver<T>, param: T): number {
  if (isFloat(resolver)) {
    return resolver;
  }
  return resolver(param);
}
